import React, { useRef, useState } from 'react';
import { RichTextToolbar, ToolbarButton, ToolbarDivider } from './RichTextToolbar';
import { useRichTextEditor } from './useRichTextEditor';
import styled from 'styled-components';
import { Border, Color, Shadow, Type } from '../../app/StyleGuide';
import ErrorMessage from '../ErrorMessage';

type RichTextEditorProps = {
  value: string;
  onChange: (newValue: string) => void;
  className?: string;
  allowedTags?: ('strong' | 'em' | 'u' | 'ul' | 'ol' | 'li' | 'p' | 'br' | 'span' | 'a' | 'img')[];
  allowedAttributes?: string[];
  placeholder?: string;
};

const RichTextEditorContainer = styled.div<{ $isDragOver?: boolean }>`
  background: white;
  border: 1px solid ${props => (props.$isDragOver ? Color.Blue._50 : Color.Blue._20)};
  border-radius: 0 0 ${Border.radius} ${Border.radius};
  border-width: 0 1px 1px 1px;
  padding: 12px;
  width: 100%;
  outline: none;
  box-shadow: inset ${Shadow.small};
  color: ${Type.Color.dark};
  min-height: 100px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  transition: border-color 0.2s ease;

  ${props =>
    props.$isDragOver &&
    `
    background-color: ${Color.Blue._10};
    border-color: ${Color.Blue._50};
    box-shadow: inset 0px 0px 0px rgba(0, 0, 0, 0), 0px 0px 0px 1px ${Color.Blue._50},
      0px 0px 0px 5px rgba(172, 211, 239, 0.3);
  `}

  &:focus {
    box-shadow: inset 0px 0px 0px rgba(0, 0, 0, 0), 0px 0px 0px 1px ${Color.Blue._50},
      0px 0px 0px 5px rgba(172, 211, 239, 0.3);
  }

  a {
    color: ${Color.Blue._50};
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
    cursor: grab;
    user-select: none;
  }

  img:active {
    cursor: grabbing;
  }

  &[data-placeholder]:empty:before {
    content: attr(data-placeholder);
    color: ${Type.Color.placeholder};
    pointer-events: none;
  }
`;

// Needed to support image uploads in Safari
const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

export const RichTextEditor = ({
  value,
  onChange,
  className,
  allowedTags = [],
  allowedAttributes = [],
  placeholder,
}: RichTextEditorProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const {
    inputRef,
    imageInputRef,
    hasRichTextTags,
    errorMessage,
    handleInput,
    handlePaste,
    handleFormatText,
    handleInsertLink,
    handleInsertImage,
    handleImageFilesSelected,
    handleDrop,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleCopy,
  } = useRichTextEditor({
    value,
    onChange,
    allowedTags,
    allowedAttributes,
  });

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the editor container itself
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDropWithFeedback = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragOver(false);
    handleDrop(e);
  };

  return (
    <div ref={containerRef} className={className}>
      <HiddenFileInput
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleImageFilesSelected}
        data-qa-hook="rich-text-editor-image-input"
      />
      {hasRichTextTags && (
        <RichTextToolbar isVisible={true} inline={true} role="toolbar">
          {allowedTags.includes('strong') && (
            <ToolbarButton onClick={() => handleFormatText('bold')} aria-label="Bold" title="Bold">
              <i className="fas fa-bold"></i>
            </ToolbarButton>
          )}
          {allowedTags.includes('em') && (
            <ToolbarButton onClick={() => handleFormatText('italic')} aria-label="Italic" title="Italic">
              <i className="fas fa-italic"></i>
            </ToolbarButton>
          )}
          {allowedTags.includes('u') && (
            <ToolbarButton onClick={() => handleFormatText('underline')} aria-label="Underline" title="Underline">
              <i className="fas fa-underline"></i>
            </ToolbarButton>
          )}
          {allowedTags.includes('ul') && (
            <ToolbarButton
              onClick={() => handleFormatText('insertUnorderedList')}
              aria-label="Bullet List"
              title="Bullet List"
            >
              <i className="fas fa-list-ul"></i>
            </ToolbarButton>
          )}
          {allowedTags.includes('ol') && (
            <ToolbarButton
              onClick={() => handleFormatText('insertOrderedList')}
              aria-label="Numbered List"
              title="Numbered List"
            >
              <i className="fas fa-list-ol"></i>
            </ToolbarButton>
          )}
          {allowedTags.includes('a') && (
            <ToolbarButton onClick={handleInsertLink} aria-label="Insert Link" title="Insert Link">
              <i className="fas fa-link"></i>
            </ToolbarButton>
          )}
          {allowedTags.includes('img') && (
            <ToolbarButton onClick={handleInsertImage} aria-label="Insert Image" title="Insert Image">
              <i className="fas fa-image"></i>
            </ToolbarButton>
          )}
          <ToolbarDivider />
          <ToolbarButton onClick={() => handleFormatText('justifyLeft')} aria-label="Align Left" title="Align Left">
            <i className="fas fa-align-left"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => handleFormatText('justifyCenter')}
            aria-label="Align Center"
            title="Align Center"
          >
            <i className="fas fa-align-center"></i>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormatText('justifyRight')} aria-label="Align Right" title="Align Right">
            <i className="fas fa-align-right"></i>
          </ToolbarButton>
        </RichTextToolbar>
      )}
      <RichTextEditorContainer
        ref={inputRef}
        contentEditable
        suppressContentEditableWarning
        data-qa-hook="rich-text-editor"
        onInput={handleInput}
        onPaste={handlePaste}
        onCopy={handleCopy}
        onDrop={handleDropWithFeedback}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        $isDragOver={isDragOver}
        data-placeholder={placeholder}
      />
      {errorMessage && (
        <ErrorMessage align="left" data-qa-hook="rich-text-editor-error">
          {errorMessage}
        </ErrorMessage>
      )}
    </div>
  );
};
